/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */
import type { AssistantSuggestedAction } from './AssistantRuntime';
import { formatScheduleWallTime } from '../../scheduler/schedule-time.utils';

export interface AssistantDraftAction {
  type: 'report' | 'automation';
  endpoint: '/actions/report-draft' | '/actions/automation-draft';
  title: string;
  draft: Record<string, unknown>;
}
const record = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));
const text = (...values: unknown[]) =>
  values
    .find((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    ?.trim() ?? '';

/** Snapshot the reviewed draft; later model/UI edits cannot change the approved request. */
export function prepareAssistantDraftAction(
  action: AssistantSuggestedAction
): AssistantDraftAction | null {
  const type =
    action.type === 'create_report_draft'
      ? 'report'
      : action.type === 'create_automation_draft'
        ? 'automation'
        : null;
  if (!type || !record(action.payload)) return null;
  const raw =
    action.payload[type === 'report' ? 'reportDraft' : 'automationDraft'] ?? action.payload.draft;
  if (!record(raw)) return null;
  try {
    const serialized = JSON.stringify(raw);
    if (new TextEncoder().encode(serialized).length > 65_536) return null;
    const draft = JSON.parse(serialized) as Record<string, unknown>;
    const title = text(draft.name, type === 'report' ? draft.reportName : undefined);
    if (!title || title.length > 255) return null;
    return {
      type,
      endpoint: type === 'report' ? '/actions/report-draft' : '/actions/automation-draft',
      title,
      draft,
    };
  } catch {
    return null;
  }
}

const COPY: Record<string, readonly string[]> = {
  tr: [
    'Rapor taslağı',
    'Otomasyon taslağı',
    'Taslağı kaydedip etkinleştirmeden önce yönetim ekranında inceleyin.',
    'Taslağı kaydet',
    'İptal',
    'Açıklama',
    'Zamanlama',
    'Alıcılar',
    'Adımlar',
    'İncelenecek taslak eksik veya geçersiz.',
    'Taslak kayıt sonucu doğrulanamadı.',
    'Taslak kaydedilemedi.',
    'Taslak kaydedildi',
    'Analiz talimatı',
  ],
  en: [
    'Report draft',
    'Automation draft',
    'Save the draft and review it in management before activation.',
    'Save draft',
    'Cancel',
    'Description',
    'Schedule',
    'Recipients',
    'Steps',
    'The draft for review is missing or invalid.',
    'The draft creation result could not be verified.',
    'The draft could not be saved.',
    'Draft saved',
    'Analysis instruction',
  ],
  ar: [
    'مسودة تقرير',
    'مسودة أتمتة',
    'احفظ المسودة وراجعها في الإدارة قبل تفعيلها.',
    'حفظ المسودة',
    'إلغاء',
    'الوصف',
    'الجدول',
    'المستلمون',
    'الخطوات',
    'المسودة المطلوب مراجعتها مفقودة أو غير صالحة.',
    'تعذر التحقق من نتيجة إنشاء المسودة.',
    'تعذر حفظ المسودة.',
    'تم حفظ المسودة',
    'تعليمات التحليل',
  ],
  de: [
    'Berichtsentwurf',
    'Automatisierungsentwurf',
    'Speichern und prüfen Sie den Entwurf vor der Aktivierung in der Verwaltung.',
    'Entwurf speichern',
    'Abbrechen',
    'Beschreibung',
    'Zeitplan',
    'Empfänger',
    'Schritte',
    'Der Entwurf fehlt oder ist ungültig.',
    'Das Ergebnis konnte nicht bestätigt werden.',
    'Der Entwurf konnte nicht gespeichert werden.',
    'Entwurf gespeichert',
    'Analyseanweisung',
  ],
  fr: [
    'Brouillon de rapport',
    'Brouillon d’automatisation',
    'Enregistrez le brouillon et vérifiez-le dans la gestion avant activation.',
    'Enregistrer le brouillon',
    'Annuler',
    'Description',
    'Planification',
    'Destinataires',
    'Étapes',
    'Le brouillon est absent ou invalide.',
    'Le résultat de création n’a pas pu être vérifié.',
    'Le brouillon n’a pas pu être enregistré.',
    'Brouillon enregistré',
    'Consigne d’analyse',
  ],
  es: [
    'Borrador de informe',
    'Borrador de automatización',
    'Guarde el borrador y revíselo en administración antes de activarlo.',
    'Guardar borrador',
    'Cancelar',
    'Descripción',
    'Programación',
    'Destinatarios',
    'Pasos',
    'Falta el borrador o no es válido.',
    'No se pudo verificar la creación del borrador.',
    'No se pudo guardar el borrador.',
    'Borrador guardado',
    'Instrucción de análisis',
  ],
  it: [
    'Bozza di report',
    'Bozza di automazione',
    'Salva la bozza e controllala nella gestione prima di attivarla.',
    'Salva bozza',
    'Annulla',
    'Descrizione',
    'Pianificazione',
    'Destinatari',
    'Passaggi',
    'La bozza è assente o non valida.',
    'Impossibile verificare la creazione della bozza.',
    'Impossibile salvare la bozza.',
    'Bozza salvata',
    'Istruzione di analisi',
  ],
  ru: [
    'Черновик отчёта',
    'Черновик автоматизации',
    'Сохраните черновик и проверьте его в управлении перед активацией.',
    'Сохранить черновик',
    'Отмена',
    'Описание',
    'Расписание',
    'Получатели',
    'Шаги',
    'Черновик отсутствует или недействителен.',
    'Не удалось подтвердить создание черновика.',
    'Не удалось сохранить черновик.',
    'Черновик сохранён',
    'Инструкция анализа',
  ],
  zh: [
    '报告草稿',
    '自动化草稿',
    '保存草稿，并在启用前到管理页面检查。',
    '保存草稿',
    '取消',
    '说明',
    '计划',
    '收件人',
    '步骤',
    '待检查的草稿缺失或无效。',
    '无法确认草稿创建结果。',
    '无法保存草稿。',
    '草稿已保存',
    '分析指令',
  ],
  ja: [
    'レポート下書き',
    '自動化下書き',
    '下書きを保存し、有効化する前に管理画面で確認してください。',
    '下書きを保存',
    'キャンセル',
    '説明',
    'スケジュール',
    '宛先',
    '手順',
    '確認用の下書きがないか無効です。',
    '下書きの作成結果を確認できませんでした。',
    '下書きを保存できませんでした。',
    '下書きを保存しました',
    '分析指示',
  ],
};
export function assistantDraftActionCopy(language?: string) {
  const values =
    COPY[
      String(language || 'tr')
        .toLowerCase()
        .split('-')[0]
    ] ?? COPY.tr;
  const [
    report,
    automation,
    review,
    save,
    cancel,
    description,
    schedule,
    recipients,
    steps,
    needsDraft,
    unconfirmed,
    failed,
    saved,
    instruction,
  ] = values;
  return {
    report,
    automation,
    review,
    save,
    cancel,
    description,
    schedule,
    recipients,
    steps,
    needsDraft,
    unconfirmed,
    failed,
    saved,
    instruction,
  };
}

// Display-only canonical draft contract. Provider validation and activation remain server-owned.
const AUTOMATION_COPY: Record<string, readonly string[]> = {
  tr: [
    'İş türü',
    'Elle',
    'Zamanlı',
    'Rapor tamamlandığında',
    'Sabit e-posta',
    'Uygulama bildirimi',
    'Rapor',
    'Dijital çalışan',
    'Dönem',
    'Çıktı',
    'Posta kutusu',
    'Başlık',
    'Mesaj',
    'En fazla çalışma',
    'Bitiş',
    'Saat dilimi',
    'saniye',
    'Olay raporu',
  ],
  en: [
    'Work type',
    'Manual',
    'Scheduled',
    'When report completes',
    'Fixed email',
    'In-app notification',
    'Report',
    'Digital worker',
    'Period',
    'Output',
    'Mailbox',
    'Subject',
    'Message',
    'Maximum runs',
    'Expires',
    'Time zone',
    'seconds',
    'Event report',
  ],
  ar: [
    'نوع العمل',
    'يدوي',
    'مجدول',
    'عند اكتمال التقرير',
    'بريد ثابت',
    'إشعار داخل التطبيق',
    'تقرير',
    'العامل الرقمي',
    'الفترة',
    'المخرجات',
    'صندوق البريد',
    'العنوان',
    'الرسالة',
    'الحد الأقصى للتشغيل',
    'الانتهاء',
    'المنطقة الزمنية',
    'ثانية',
    'تقرير الحدث',
  ],
  de: [
    'Aufgabentyp',
    'Manuell',
    'Geplant',
    'Nach Berichtabschluss',
    'Feste E-Mail',
    'App-Benachrichtigung',
    'Bericht',
    'Digitaler Mitarbeiter',
    'Zeitraum',
    'Ausgabe',
    'Postfach',
    'Betreff',
    'Nachricht',
    'Maximale Ausführungen',
    'Ablauf',
    'Zeitzone',
    'Sekunden',
    'Ereignisbericht',
  ],
  fr: [
    'Type de tâche',
    'Manuel',
    'Planifié',
    'À la fin du rapport',
    'E-mail fixe',
    'Notification intégrée',
    'Rapport',
    'Agent numérique',
    'Période',
    'Sortie',
    'Boîte mail',
    'Objet',
    'Message',
    'Exécutions maximales',
    'Expiration',
    'Fuseau horaire',
    'secondes',
    'Rapport déclencheur',
  ],
  es: [
    'Tipo de trabajo',
    'Manual',
    'Programado',
    'Al finalizar el informe',
    'Correo fijo',
    'Notificación interna',
    'Informe',
    'Trabajador digital',
    'Periodo',
    'Salida',
    'Buzón',
    'Asunto',
    'Mensaje',
    'Ejecuciones máximas',
    'Vencimiento',
    'Zona horaria',
    'segundos',
    'Informe del evento',
  ],
  it: [
    'Tipo di lavoro',
    'Manuale',
    'Pianificato',
    'Al completamento del report',
    'Email fissa',
    'Notifica interna',
    'Report',
    'Lavoratore digitale',
    'Periodo',
    'Output',
    'Casella email',
    'Oggetto',
    'Messaggio',
    'Esecuzioni massime',
    'Scadenza',
    'Fuso orario',
    'secondi',
    'Report evento',
  ],
  ru: [
    'Тип работы',
    'Вручную',
    'По расписанию',
    'После завершения отчёта',
    'Фиксированное письмо',
    'Уведомление в приложении',
    'Отчёт',
    'Цифровой сотрудник',
    'Период',
    'Результат',
    'Почтовый ящик',
    'Тема',
    'Сообщение',
    'Максимум запусков',
    'Окончание',
    'Часовой пояс',
    'секунд',
    'Отчёт события',
  ],
  zh: [
    '任务类型',
    '手动',
    '定时',
    '报告完成时',
    '固定邮件',
    '应用内通知',
    '报告',
    '数字员工',
    '期间',
    '输出',
    '邮箱',
    '主题',
    '消息',
    '最大运行次数',
    '到期时间',
    '时区',
    '秒',
    '事件报告',
  ],
  ja: [
    '作業の種類',
    '手動',
    'スケジュール',
    'レポート完了時',
    '固定メール',
    'アプリ内通知',
    'レポート',
    'デジタル従業員',
    '期間',
    '出力',
    'メールボックス',
    '件名',
    'メッセージ',
    '最大実行回数',
    '有効期限',
    'タイムゾーン',
    '秒',
    'イベントレポート',
  ],
};
const PERIOD_COPY: Record<string, Record<string, string>> = {
  tr: {
    TODAY: 'Bugün',
    YESTERDAY: 'Dün',
    THIS_WEEK: 'Bu hafta',
    PREVIOUS_WEEK: 'Önceki hafta',
    LAST_7_DAYS: 'Son 7 gün',
    THIS_MONTH: 'Bu ay',
    PREVIOUS_MONTH: 'Önceki ay',
    THIS_YEAR: 'Bu yıl',
    PREVIOUS_YEAR: 'Önceki yıl',
  },
  en: {
    TODAY: 'Today',
    YESTERDAY: 'Yesterday',
    THIS_WEEK: 'This week',
    PREVIOUS_WEEK: 'Previous week',
    LAST_7_DAYS: 'Last 7 days',
    THIS_MONTH: 'This month',
    PREVIOUS_MONTH: 'Previous month',
    THIS_YEAR: 'This year',
    PREVIOUS_YEAR: 'Previous year',
  },
  ar: {
    TODAY: 'اليوم',
    YESTERDAY: 'أمس',
    THIS_WEEK: 'هذا الأسبوع',
    PREVIOUS_WEEK: 'الأسبوع السابق',
    LAST_7_DAYS: 'آخر 7 أيام',
    THIS_MONTH: 'هذا الشهر',
    PREVIOUS_MONTH: 'الشهر السابق',
    THIS_YEAR: 'هذا العام',
    PREVIOUS_YEAR: 'العام السابق',
  },
  de: {
    TODAY: 'Heute',
    YESTERDAY: 'Gestern',
    THIS_WEEK: 'Diese Woche',
    PREVIOUS_WEEK: 'Vorige Woche',
    LAST_7_DAYS: 'Letzte 7 Tage',
    THIS_MONTH: 'Dieser Monat',
    PREVIOUS_MONTH: 'Voriger Monat',
    THIS_YEAR: 'Dieses Jahr',
    PREVIOUS_YEAR: 'Voriges Jahr',
  },
  fr: {
    TODAY: 'Aujourd’hui',
    YESTERDAY: 'Hier',
    THIS_WEEK: 'Cette semaine',
    PREVIOUS_WEEK: 'Semaine précédente',
    LAST_7_DAYS: '7 derniers jours',
    THIS_MONTH: 'Ce mois-ci',
    PREVIOUS_MONTH: 'Mois précédent',
    THIS_YEAR: 'Cette année',
    PREVIOUS_YEAR: 'Année précédente',
  },
  es: {
    TODAY: 'Hoy',
    YESTERDAY: 'Ayer',
    THIS_WEEK: 'Esta semana',
    PREVIOUS_WEEK: 'Semana anterior',
    LAST_7_DAYS: 'Últimos 7 días',
    THIS_MONTH: 'Este mes',
    PREVIOUS_MONTH: 'Mes anterior',
    THIS_YEAR: 'Este año',
    PREVIOUS_YEAR: 'Año anterior',
  },
  it: {
    TODAY: 'Oggi',
    YESTERDAY: 'Ieri',
    THIS_WEEK: 'Questa settimana',
    PREVIOUS_WEEK: 'Settimana precedente',
    LAST_7_DAYS: 'Ultimi 7 giorni',
    THIS_MONTH: 'Questo mese',
    PREVIOUS_MONTH: 'Mese precedente',
    THIS_YEAR: 'Quest’anno',
    PREVIOUS_YEAR: 'Anno precedente',
  },
  ru: {
    TODAY: 'Сегодня',
    YESTERDAY: 'Вчера',
    THIS_WEEK: 'Эта неделя',
    PREVIOUS_WEEK: 'Предыдущая неделя',
    LAST_7_DAYS: 'Последние 7 дней',
    THIS_MONTH: 'Этот месяц',
    PREVIOUS_MONTH: 'Предыдущий месяц',
    THIS_YEAR: 'Этот год',
    PREVIOUS_YEAR: 'Предыдущий год',
  },
  zh: {
    TODAY: '今天',
    YESTERDAY: '昨天',
    THIS_WEEK: '本周',
    PREVIOUS_WEEK: '上周',
    LAST_7_DAYS: '过去7天',
    THIS_MONTH: '本月',
    PREVIOUS_MONTH: '上月',
    THIS_YEAR: '今年',
    PREVIOUS_YEAR: '去年',
  },
  ja: {
    TODAY: '今日',
    YESTERDAY: '昨日',
    THIS_WEEK: '今週',
    PREVIOUS_WEEK: '先週',
    LAST_7_DAYS: '過去7日',
    THIS_MONTH: '今月',
    PREVIOUS_MONTH: '先月',
    THIS_YEAR: '今年',
    PREVIOUS_YEAR: '昨年',
  },
};
function automationDraftReviewFields(draft: Record<string, unknown>, language?: string) {
  const locale = String(language || 'tr')
    .toLowerCase()
    .split('-')[0];
  const labels = AUTOMATION_COPY[locale] ?? AUTOMATION_COPY.tr;
  const periods = PERIOD_COPY[locale] ?? PERIOD_COPY.tr;
  const [
    kind,
    manual,
    scheduled,
    event,
    email,
    notification,
    report,
    worker,
    period,
    output,
    mailbox,
    subject,
    message,
    maxRuns,
    expires,
    timeZone,
    seconds,
    eventReport,
  ] = labels;
  const copy = assistantDraftActionCopy(language);
  const fields: Array<{ label: string; value: string }> = [];
  const add = (label: string, value: unknown) => {
    if (
      (typeof value === 'string' && value.trim()) ||
      (typeof value === 'number' && Number.isFinite(value))
    )
      fields.push({ label, value: String(value) });
  };
  const action = record(draft.action) ? draft.action : {},
    trigger = record(draft.trigger) ? draft.trigger : {};
  add(
    kind,
    action.kind === 'REPORT'
      ? report
      : action.kind === 'FIXED_EMAIL'
        ? email
        : action.kind === 'NOTIFICATION'
          ? notification
          : undefined
  );
  add(
    copy.schedule,
    trigger.kind === 'MANUAL'
      ? manual
      : trigger.kind === 'SCHEDULE'
        ? scheduled
        : trigger.kind === 'EVENT'
          ? event
          : undefined
  );
  if (record(trigger.schedule)) {
    const schedule = trigger.schedule;
    add(timeZone, schedule.timezone);
    if (schedule.kind === 'CRON') add(copy.schedule + ' · cron', schedule.cronExpression);
    if (schedule.kind === 'INTERVAL' && typeof schedule.intervalSeconds === 'number')
      add(copy.schedule + ' · ' + seconds, schedule.intervalSeconds);
    if (
      schedule.kind === 'ONE_TIME' &&
      typeof schedule.runAt === 'string' &&
      typeof schedule.timezone === 'string'
    ) {
      try {
        add(
          copy.schedule + ' · ' + timeZone,
          formatScheduleWallTime(new Date(schedule.runAt), schedule.timezone).replace('T', ' ')
        );
      } catch {
        add(copy.schedule + ' · ISO', schedule.runAt);
      }
    }
  }
  if (trigger.kind === 'EVENT') add(eventReport, trigger.reportId);
  if (action.kind === 'REPORT') {
    add(report, action.reportId);
    add(worker, action.digitalWorkerId);
    if (record(action.period))
      add(
        period,
        (periods[text(action.period.kind)] ?? text(action.period.kind)) +
          ' · ' +
          text(action.period.dimension)
      );
    if (record(action.output))
      add(output, text(action.output.format) + ' · ' + text(action.output.locale));
  }
  add(subject, action.subject ?? action.title);
  add(message, action.message);
  const target = action.kind === 'REPORT' && record(action.email) ? action.email : action;
  add(mailbox, target.mailboxRef);
  if (Array.isArray(target.userIds))
    add(
      copy.recipients,
      target.userIds
        .filter((id) => typeof id === 'string')
        .map((id) => '#' + id)
        .join(', ')
    );
  if (record(draft.limits)) {
    add(maxRuns, draft.limits.maxRuns);
    add(expires, draft.limits.expiresAt);
  }
  return fields;
}

export function assistantDraftReviewFields(action: AssistantDraftAction, language?: string) {
  const copy = assistantDraftActionCopy(language),
    draft = action.draft;
  const canonical = action.type === 'automation' && draft.version === 1;
  const fields = [
    { label: copy.description, value: text(draft.description, draft.notes) },
    { label: copy.schedule, value: canonical ? '' : text(draft.cronExpression, draft.eventName) },
    { label: copy.recipients, value: canonical ? '' : text(draft.toEmails) },
  ].filter((field) => field.value);
  if (action.type === 'report' && text(draft.analysisInstruction))
    fields.unshift({ label: copy.instruction, value: text(draft.analysisInstruction) });
  if (canonical) fields.push(...automationDraftReviewFields(draft, language));
  if (action.type === 'automation' && !canonical && Array.isArray(draft.steps)) {
    fields.push({
      label: copy.steps,
      value: draft.steps
        .map(
          (step, index) =>
            `${index + 1}. ${record(step) ? text(step.name) || `${copy.steps} ${index + 1}` : `${copy.steps} ${index + 1}`}`
        )
        .join('\n'),
    });
  }
  return fields;
}

/** HTTP success alone is not proof of a saved, inactive business draft. */
export function readAssistantDraftActionResult(
  action: AssistantDraftAction,
  value: unknown,
  language?: string
) {
  const copy = assistantDraftActionCopy(language);
  if (
    !record(value) ||
    value.created !== true ||
    value.type !== action.type ||
    !/^[1-9]\d{0,18}$/.test(String(value.id ?? '')) ||
    BigInt(String(value.id)) > 9223372036854775807n ||
    (typeof value.id === 'number' && !Number.isSafeInteger(value.id)) ||
    !record(value.item) ||
    value.item.isActive !== false ||
    String(value.item[action.type === 'report' ? 'Id' : 'id']) !== String(value.id)
  )
    throw new Error(copy.unconfirmed);
  return { id: String(value.id), message: `${copy.saved}: ${action.title} (#${value.id}).` };
}
